import { z } from "zod";
import { ROLES } from "../rbac.js";
import {
  booleanQuery,
  email,
  phoneE164,
  uuidParam,
  paginationQuery,
  slug,
  timezone,
  trimmedString,
} from "./common.js";

/* ───────────────────────────── Companies ───────────────────────────── */

/**
 * Contact details are all optional here and nullable in the database, because
 * `POST /auth/workspace` creates a company from a name alone. Requiring them
 * would put fields in front of a first-run user who has not yet seen anything
 * work; they are filled in from company settings afterwards.
 */
const companyContact = {
  email: email.nullable().optional(),
  phone: phoneE164.nullable().optional(),
  website: z.string().trim().url().max(2048).nullable().optional(),
  /** A plain URL for now. Uploads land in Phase 6 and will write a URL here too. */
  logo_url: z.string().trim().url().max(2048).nullable().optional(),
};

/**
 * `POST /companies` - a new tenant, **and its first site in the same request**.
 *
 * The site is not optional, and that is a fix rather than a preference. A
 * company with no sites is a tenant nobody can enter: the console works one site
 * at a time (decisions #20), so `ScopeSwitcher` cannot switch into a company
 * whose site list is empty - it has nothing to select - and the Sites screen
 * creates into whichever company the console is currently pointed at. A
 * superadmin who created a company on its own therefore had no route to give it
 * a site, and no route to invite anybody into it either, because an invitation
 * must name a home site. The row existed and nothing could be done with it.
 *
 * `create_workspace` already had this right for self sign-up - company and site
 * in one statement, because one without the other is not a workspace. This is
 * the same rule for the path a superadmin takes.
 */
export const createCompanySchema = z.object({
  name: trimmedString(2, 120),
  slug,
  ...companyContact,
  settings: z.record(z.unknown()).default({}),
  site: z.object({
    name: trimmedString(2, 120),
    timezone,
  }),
});
export type CreateCompanyInput = z.infer<typeof createCompanySchema>;

/**
 * `is_active` is here rather than in `createCompanySchema` because a company is
 * always created live. It is superadmin-only, and that is enforced by
 * `app.enforce_company_write_rules` in Postgres rather than by this schema -
 * RLS cannot restrict a single column, and an admin legitimately needs UPDATE on
 * the rest of the row.
 */
export const updateCompanySchema = createCompanySchema
  // The first site belongs to creation only. Left in, a PATCH carrying `site`
  // would validate cleanly and then be silently dropped by the controller.
  .omit({ site: true })
  .partial()
  .extend({
    is_active: z.boolean().optional(),
  });
export type UpdateCompanyInput = z.infer<typeof updateCompanySchema>;

/**
 * `GET /companies`.
 *
 * This used to be bare `paginationQuery`, which meant the console's Companies
 * screen was the one list in the product that could search but not filter -
 * while `is_active` had been a real, settable column on the row for as long as
 * the deactivate button had existed. A superadmin looking for the tenant they
 * suspended last week had to page through the live ones to find it.
 *
 * Omitted means both, the same rule `listSitesQuery` states: a deactivated
 * company is still a company, and a filter that silently hid them would make
 * the row disappear the moment somebody used the button beside it.
 */
export const listCompaniesQuery = paginationQuery.extend({
  is_active: booleanQuery.optional(),
});
export type ListCompaniesQuery = z.infer<typeof listCompaniesQuery>;

/* ─────────────────────────────── Sites ─────────────────────────────── */

export const createSiteSchema = z.object({
  /**
   * Optional for an admin - the API fills it from the caller's own company,
   * and RLS rejects any other value anyway. Required in practice only for a
   * superadmin, who has no home company to default to.
   */
  company_id: uuidParam.optional(),
  name: trimmedString(2, 120),
  timezone,
});
export type CreateSiteInput = z.infer<typeof createSiteSchema>;

/**
 * `GET /sites`.
 *
 * `company_id` is honoured for an unbounded caller only - see `filterByCompany`
 * - and exists for one screen: the superadmin's company switcher, which has to
 * list a company's sites *before* one of them has been selected as the active
 * site. Everyone else's company is already pinned by their scope, so passing it
 * changes nothing rather than being rejected.
 */
export const listSitesQuery = paginationQuery.extend({
  company_id: uuidParam.optional(),
  /** Active or deactivated. Omitted means both: a deactivated site is still a site. */
  is_active: booleanQuery.optional(),
});
export type ListSitesQuery = z.infer<typeof listSitesQuery>;

export const updateSiteSchema = z.object({
  name: trimmedString(2, 120).optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  /**
   * Closing or reopening a yard - ordinary admin work, unlike the company
   * switch. Postgres refuses to close a site anyone still defaults to, which
   * surfaces here as a 409 naming the number of users to move first.
   */
  is_active: z.boolean().optional(),
});
export type UpdateSiteInput = z.infer<typeof updateSiteSchema>;

/* ─────────────────────────────── Users ─────────────────────────────── */

export const roleName = z.enum(ROLES);

/**
 * Inviting a user into an **existing** company.
 *
 * One of two ways an account comes into existence; the other is self sign-up,
 * which creates a company rather than joining one (see `createWorkspaceSchema`).
 *
 * An existing user with `user.invite` fills in the invitee's details and
 * tenancy. Supabase Auth emails the invite link and creates the identity, then
 * the API writes the `users` row through the inviter's own RLS-scoped client -
 * so Postgres, via the `users_insert` policy, validates that they may provision
 * into that company and may hand out that role. The invitee sets their own
 * password from the link.
 *
 * `site_id` is required for every role, superadmin included: every user has a
 * home site, and it is where sign-in lands them. `company_id` is optional only
 * because an admin's is filled in from their own - a superadmin must state it.
 *
 * **One site, deliberately.** This used to carry a list of extra sites to grant,
 * which was a second and weaker copy of the membership grant: validated by a
 * trigger instead of by the `site_memberships` INSERT policy, which checks that
 * the granting admin may administer the user *and* see the site. Widen reach with
 * `PUT /users/:id/sites` once the person exists - which is usually when their
 * actual coverage is known anyway.
 *
 * There is no separate invitation resource. This creates a `users` row with
 * `status: "invited"`; the lifecycle from there is `status`.
 */
export const inviteUserSchema = z.object({
  email,
  full_name: trimmedString(2, 120),
  role: roleName,
  company_id: uuidParam.optional(),
  /** The invitee's home site - where sign-in lands them, and the only one granted. */
  site_id: uuidParam,
  phone: phoneE164.nullable().optional(),
  timezone,
});
export type InviteUserInput = z.infer<typeof inviteUserSchema>;

/**
 * `PUT /users/:id/sites` - replace a user's site assignments wholesale.
 *
 * A replace rather than add/remove endpoints, because the console edits this as
 * a checkbox list and a diff computed on the client is a diff that can be wrong.
 * Sending the full intended set makes the request idempotent and makes a lost
 * response harmless to retry.
 *
 * The user's default site does not need to be listed and cannot be removed -
 * the database refuses to delete it (`app.protect_home_site_membership`).
 * Change the default first if that is what you meant.
 */
export const assignSitesSchema = z.object({
  site_ids: z.array(uuidParam).max(200),
});
export type AssignSitesInput = z.infer<typeof assignSitesSchema>;

export const updateUserSchema = z.object({
  full_name: trimmedString(2, 120).optional(),
  role: roleName.optional(),
  company_id: uuidParam.optional(),
  /**
   * Their **default** site - the one sign-in lands them on, badged "Default" in
   * the console. Setting it also grants a membership if they did not have one,
   * so promoting someone's default to a site they could not previously reach is
   * one call rather than two. Site *reach* is edited at `PUT /users/:id/sites`.
   */
  site_id: uuidParam.optional(),
  phone: phoneE164.nullable().optional(),
  timezone: z.string().trim().min(1).max(64).optional(),
  /**
   * Revocation and reinstatement. `invited` is not offered: it describes what
   * has happened to an account, not something an administrator decides, and the
   * database moves an account out of it on first sign-in.
   */
  status: z.enum(["active", "disabled"]).optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/**
 * `GET /sites/:siteId/users`.
 *
 * Extends `paginationQuery` rather than re-stating `page`/`pageSize`/`search`,
 * which is what it used to do twelve lines below a sibling that did it properly.
 * Identical values, so nothing behaved differently - but a pagination contract
 * written down twice is one that eventually disagrees with itself.
 *
 * No `site_id` and no `company_id`. The site travels in the path (decisions #20)
 * and a site belongs to exactly one company, so both were accepted, documented
 * and then silently ignored by the only handler that reads this schema.
 */
export const listUsersQuery = paginationQuery.extend({
  role: roleName.optional(),
  status: z.enum(["invited", "active", "disabled"]).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuery>;
